import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/brand";

const SectionLabel = ({ children }) => <span className="haven-label">{children}</span>;

export default function Snapshot() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0); // 0..N-1, then N=email, N+1=result
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [state, setState] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    api.get("/quiz/questions").then((r) => setQuestions(r.data.questions || [])).catch(() => {});
  }, []);

  const total = questions.length;
  const isEmailStep = step === total;
  const isResultStep = step === total + 1;
  const current = !isEmailStep && !isResultStep ? questions[step] : null;

  const choose = (qid, opt) => {
    setAnswers((a) => ({ ...a, [qid]: opt }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submit = async () => {
    if (!email || !email.includes("@")) {
      setErrMsg("Please enter a valid email.");
      setState("error");
      return;
    }
    setErrMsg("");
    setState("loading");
    try {
      const payload = {
        email,
        answers: Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer })),
      };
      const { data } = await api.post("/quiz/submit", payload);
      setResult(data);
      setStep(total + 1);
      setState("done");
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Snapshot engine returned an error. Try again.");
      setState("error");
    }
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setEmail("");
    setResult(null);
    setState("idle");
  };

  const progressPct = total > 0 ? Math.min(100, Math.round((step / (total + 1)) * 100)) : 0;

  return (
    <main data-testid="snapshot-page" className="haven-page-shell" style={{ paddingTop: 32 }}>
      <SectionLabel>Free · 8 questions · 2 minutes</SectionLabel>
      <h1
        className="font-serif mt-3"
        style={{ fontSize: "clamp(36px, 5vw, 56px)", lineHeight: 1.05, color: "var(--ink)", letterSpacing: "-0.01em" }}
      >
        Confidence Snapshot.
      </h1>
      <p className="mt-3" style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7, maxWidth: 640 }}>
        Eight honest questions. AI-scored on Daniel's framework. You'll learn your gap pattern and which Haven modules
        would actually move you forward — without the flattery and without the fluff.
      </p>

      {/* progress */}
      <div className="mt-8" data-testid="snapshot-progress">
        <div style={{ height: 6, borderRadius: 999, background: "var(--border-soft)", overflow: "hidden" }}>
          <div
            style={{
              width: `${progressPct}%`,
              height: "100%",
              background: "var(--gold)",
              transition: "width 320ms cubic-bezier(.2,.7,.2,1)",
            }}
          />
        </div>
        <div className="mt-2" style={{ fontSize: 11, color: "var(--ink-muted)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Step {Math.min(step + 1, total + 2)} of {total + 2}
        </div>
      </div>

      {/* Question */}
      {current && (
        <div data-testid={`snapshot-question-${step}`} className="haven-card fade-up mt-5" style={{ padding: 26 }}>
          <span className="haven-bubble">Question {step + 1}</span>
          <h2 className="font-serif mt-4" style={{ fontSize: "clamp(22px, 2.6vw, 30px)", lineHeight: 1.2, color: "var(--ink)" }}>
            {current.text}
          </h2>
          <div className="mt-6 grid gap-3">
            {current.options.map((opt, i) => {
              const selected = answers[current.id] === opt;
              return (
                <button
                  key={i}
                  data-testid={`snapshot-option-${step}-${i}`}
                  onClick={() => choose(current.id, opt)}
                  className="haven-card text-left"
                  style={{
                    padding: "14px 18px",
                    border: selected ? "2px solid var(--gold)" : "1px solid var(--border-soft)",
                    background: selected ? "var(--gold-faint)" : "var(--surface-card)",
                    cursor: "pointer",
                    transition: "border-color 200ms ease, background 200ms ease, transform 200ms ease",
                  }}
                >
                  <span style={{ color: "var(--ink)", fontSize: 14, lineHeight: 1.5 }}>{opt}</span>
                </button>
              );
            })}
          </div>
          {step > 0 && (
            <button
              data-testid="snapshot-back"
              className="mt-5 haven-btn haven-btn-ghost"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              ← Back
            </button>
          )}
        </div>
      )}

      {/* Email step */}
      {isEmailStep && !result && (
        <div data-testid="snapshot-email-step" className="haven-card fade-up mt-5" style={{ padding: 26 }}>
          <span className="haven-bubble">Almost there</span>
          <h2 className="font-serif mt-3" style={{ fontSize: "clamp(22px, 2.6vw, 30px)", lineHeight: 1.2, color: "var(--ink)" }}>
            Where should we send your snapshot?
          </h2>
          <p style={{ marginTop: 8, color: "var(--ink-soft)", fontSize: 13.5 }}>
            We email you the result plus the 5-email gap primer. No spam. Unsubscribe anytime.
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <input
              data-testid="snapshot-email-input"
              className="haven-input"
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              data-testid="snapshot-submit"
              onClick={submit}
              className="haven-btn haven-btn-primary"
              disabled={state === "loading"}
            >
              {state === "loading" ? "Scoring…" : "See my profile"}
            </button>
          </div>
          {state === "error" && (
            <div data-testid="snapshot-error" style={{ color: "var(--rose)", fontSize: 12, marginTop: 8 }}>
              {errMsg}
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {isResultStep && result && (
        <div data-testid="snapshot-result" className="mt-6 grid gap-4">
          <div className="haven-card haven-card-gold" style={{ padding: 26 }}>
            <SectionLabel>Your profile</SectionLabel>
            <h2 className="font-serif mt-3" style={{ fontSize: "clamp(28px, 3.6vw, 40px)", lineHeight: 1.1, color: "var(--ink)" }}>
              {result.profile_title}
            </h2>
            <p style={{ marginTop: 10, color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7 }}>{result.profile_summary}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="haven-card" style={{ padding: 22 }}>
              <SectionLabel>Gap pattern</SectionLabel>
              <ul className="mt-3 space-y-2" style={{ color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.6 }}>
                {(result.knowledge_gaps || []).map((g, i) => (
                  <li key={i} style={{ display: "flex", gap: 10 }}>
                    <span style={{ color: "var(--rose)", minWidth: 14 }}>·</span>
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="haven-card" style={{ padding: 22 }}>
              <SectionLabel>Modules that would actually help you</SectionLabel>
              <div className="mt-3 flex flex-wrap gap-2">
                {(result.recommended_modules || []).map((m, i) => (
                  <span key={i} className="haven-bubble" style={{ background: "var(--sage-light)" }}>{m}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="haven-card" style={{ padding: 22, background: "var(--ink)", color: "var(--warm-white)", borderColor: "var(--ink)" }}>
            <SectionLabel>Daniel's next step for you</SectionLabel>
            <p className="font-serif italic" style={{ marginTop: 8, fontSize: 18, lineHeight: 1.5 }}>
              "{result.next_step}"
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/join" className="haven-btn haven-btn-gold" data-testid="snapshot-cta-join">
                Lock founding rate
              </Link>
              <button onClick={restart} className="haven-btn haven-btn-ghost" data-testid="snapshot-restart" style={{ background: "transparent", color: "var(--warm-white)", borderColor: "rgba(251,248,242,0.3)" }}>
                Retake snapshot
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
